import { Autocomplete, Box, Button, Checkbox, OutlinedInput, TextField, Typography } from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { useCallback, useState } from 'react';
import { BasicDialog } from '../../../components/Dialog/BasicDialog';
import { SourcingList } from '../../../schemas/SourcingList.schema';
import { colors } from '../../../theme/colors';
import { dialogInputSx } from '../sourcing-dialog.styles';

interface AddToListDialogProps {
  readonly open: boolean;
  readonly entityCount: number;
  readonly entityType: 'company' | 'person';
  readonly lists: SourcingList[];
  readonly isLoading: boolean;
  readonly onConfirm: (listIds: string[], newListName?: string) => void;
  readonly onClose: () => void;
}

const entityLabels = {
  company: { singular: 'company', plural: 'companies' },
  person: { singular: 'person', plural: 'people' },
};

const CREATE_OPTION_ID = '__create__';

type ListOption = SourcingList | { id: typeof CREATE_OPTION_ID; name: string };

function isCreateOption(option: ListOption): option is { id: typeof CREATE_OPTION_ID; name: string } {
  return option.id === CREATE_OPTION_ID;
}

export function AddToListDialog({
  open,
  entityCount,
  entityType,
  lists,
  isLoading,
  onConfirm,
  onClose,
}: AddToListDialogProps) {
  const [selectedLists, setSelectedLists] = useState<SourcingList[]>([]);
  const [isCreating, setIsCreating] = useState(false);
  const [newListName, setNewListName] = useState('');

  const { singular, plural } = entityLabels[entityType];
  const label = entityCount === 1 ? `1 ${singular}` : `${entityCount} ${plural}`;

  const options: ListOption[] = [{ id: CREATE_OPTION_ID, name: 'Create New Static List' }, ...lists];

  const canConfirm =
    (selectedLists.length > 0 || newListName.trim().length > 0) &&
    (!isCreating || newListName.trim().length > 0);

  const handleConfirm = useCallback(() => {
    if (!canConfirm) {
      return;
    }
    const listIds = selectedLists.map((l) => l.id);
    const trimmedName = newListName.trim();
    onConfirm(listIds, isCreating && trimmedName ? trimmedName : undefined);
  }, [canConfirm, selectedLists, newListName, isCreating, onConfirm]);

  const handleClose = useCallback(() => {
    setSelectedLists([]);
    setIsCreating(false);
    setNewListName('');
    onClose();
  }, [onClose]);

  const handleCancelCreate = useCallback(() => {
    setIsCreating(false);
    setNewListName('');
  }, []);

  const handleAutocompleteChange = useCallback((_event: unknown, value: ListOption[]) => {
    const hasCreate = value.some(isCreateOption);
    if (hasCreate) {
      setIsCreating(true);
      setSelectedLists(value.filter((v): v is SourcingList => !isCreateOption(v)));
    } else {
      setSelectedLists(value as SourcingList[]);
    }
  }, []);

  return (
    <BasicDialog
      open={open}
      onClose={handleClose}
      title={`Add ${label} to`}
      lightDismiss
      sx={{ '& .MuiDialog-paper': { width: 696, maxWidth: 696 } }}
    >
      <Box sx={{ px: '2.5rem', pt: '0.25rem', pb: '0.75rem' }}>
        <Autocomplete
          multiple
          disableCloseOnSelect
          disabled={isCreating}
          options={options}
          value={selectedLists}
          getOptionLabel={(opt) => opt.name}
          isOptionEqualToValue={(opt, val) => opt.id === val.id}
          onChange={handleAutocompleteChange}
          noOptionsText={
            <Typography variant='caption' color='text.secondary'>
              No lists available
            </Typography>
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label='List'
              required
              size='small'
              placeholder={selectedLists.length === 0 ? 'Select an existing list or create a new one' : ''}
            />
          )}
          renderOption={(props, option, { selected }) => {
            if (isCreateOption(option)) {
              return (
                <li {...props} key={option.id}>
                  <Typography variant='body2' color='secondary' sx={{ fontWeight: 400 }}>
                    Create New Static List
                  </Typography>
                </li>
              );
            }
            return (
              <li {...props} key={option.id}>
                <Checkbox
                  icon={<CheckBoxOutlineBlankIcon fontSize='small' />}
                  checkedIcon={<CheckBoxIcon fontSize='small' />}
                  sx={{ mr: 1 }}
                  checked={selected}
                />
                {option.name}
              </li>
            );
          }}
          renderTags={(value) => {
            if (value.length === 0) {
              return null;
            }
            const names = value.map((v) => v.name);
            return (
              <Typography variant='body2' noWrap sx={{ maxWidth: '80%' }}>
                {names.join(', ')}
              </Typography>
            );
          }}
        />
      </Box>

      {isCreating && (
        <Box
          sx={{
            mx: '2.5rem',
            mb: '0.75rem',
            p: '0.75rem 1rem 1rem',
            border: '0.5px solid',
            borderColor: colors.border.card,
            borderRadius: '4px',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: '0.75rem' }}>
            <Typography variant='body2'>Create New Static List</Typography>
            <Button
              variant='text'
              color='secondary'
              size='small'
              aria-label='Cancel list creation'
              onClick={handleCancelCreate}
            >
              Cancel
            </Button>
          </Box>
          <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mb: '0.25rem' }}>
            List Name <span style={{ color: '#d04246' }}>*</span>
          </Typography>
          <OutlinedInput
            autoFocus
            fullWidth
            size='small'
            value={newListName}
            onChange={(e) => setNewListName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && canConfirm) {
                handleConfirm();
              }
            }}
            sx={dialogInputSx}
          />
        </Box>
      )}

      <Box sx={{ display: 'flex', gap: '0.75rem', px: '2.5rem', pt: '1.25rem', pb: '2rem' }}>
        <Button
          variant='contained'
          color='secondary'
          disabled={!canConfirm || isLoading}
          onClick={handleConfirm}
        >
          Add
        </Button>
        <Button
          variant='outlined'
          color='secondary'
          aria-label='Cancel and close dialog'
          onClick={handleClose}
        >
          Cancel
        </Button>
      </Box>
    </BasicDialog>
  );
}
